import qs from 'qs';

import type {
  CreateEnvelopeRequest,
  FilteredAcceptedInvoicesPagedRequest,
  UploadScansRequest,
} from '~/common/types/finance/inbound/hoa/inboundHoaInvoiceRequests';
import {
  type CreateRepairSalesInvoicePaymentRequest,
  type CreateRepairSalesInvoiceRequest,
  type LinkRepairPaymentToRepairPaymentRequestRequest,
  type SetRepairPaymentRequestOnHoldRequest,
  type UpdateRepairPaymentRequestFlowStatusRequest,
} from '~/common/types/finance/outbound/repair/repairSalesInvoiceRequests';
import { type PaymentSettings, type PurchaseInvoiceSettings } from '~/common/types/finance';
import {
  type RepairSalesInvoice,
  type RepairSalesInvoicePaymentRequestStatus,
} from '~/common/types/finance/outbound/repair/repairSalesInvoices';
import { type AcceptedInvoiceResponse } from '~/common/types/finance/inbound/hoa/inboundHoaInvoices';
import type { CreateInboundHoaUblUploadResponse } from '~/common/types/finance/inbound/hoa/inboundHoaUblUpload';
import { type DebtorSettings } from '~/common/types/finance/debtorSettings';
import { type File } from '@/types/documents';
import { type PagedResult } from '~/common/types';
import { type ProvisionSettingsDeleteRequest } from '~/common/types/finance/provisionSettings/provisionSettingsDeleteRequest';
import { type ProvisionSettingsFormFields } from '~/common/types/finance/provisionSettings/useProvisionSettingsValidationSchema';
import { type ProvisionSettingsType } from '~/common/types/finance/provisionSettings/provisionSettings';
import { type ProvisionSettingsVariables } from '~/common/types/finance/provisionSettings/provisionSettingsVariables';
import { type Spreadsheet } from '@/types/finance';
import { type UpdateDebtorSettingsRequest } from '~/common/types/finance/debtorSettingRequests';
import { type UpdateFinanceRemarkRequest } from '~/common/types/finance/financeRequests';

import config from '@/config/config';
import createRestClient from '../restClient';

const restClient = createRestClient();
const client = createRestClient(config.baseURL);

const createManualCorrection = (data: unknown) =>
  restClient.post('finance/manual-correction', data);

const createManualPayment = (data: unknown) => restClient.post('finance/payment', data);

const deletePayment = (paymentId: string) => restClient.delete(`finance/payment/${paymentId}`);

const deleteSpreadsheet = (spreadsheetId: string) =>
  restClient.delete(`finance/spreadsheet/${spreadsheetId}`);

const getCaseGroupBalance = (
  caseGroupId: string,
): Promise<{ salesNet: number; purchaseNet: number; chargedFee: number; balance: number }> =>
  restClient.get(`finance/${caseGroupId}/balance`);

const getManualCorrections = (caseGroupId: string): Promise<{ amount: number; reason: string }[]> =>
  restClient.get(`finance/manual-corrections?caseGroupId=${caseGroupId}`);

const getSpreadsheet: (spreadsheetId: string) => Promise<Spreadsheet> = (spreadsheetId) =>
  restClient.get(`finance/${spreadsheetId}`);

const getSpreadsheetFiles: (spreadsheetId: string) => Promise<File[]> = (spreadsheetId) =>
  restClient.get(`finance/${spreadsheetId}/file.list`);

const getSpreadsheetsByCaseGroup: (caseGroupId: string) => Promise<Spreadsheet[]> = (caseGroupId) =>
  restClient.get(`finance/list?caseGroupId=${caseGroupId}`);

const updateSpreadsheetPublicity = (data: unknown) => restClient.put('finance/public', data);

const updateSpreadsheetRemark = (data: UpdateFinanceRemarkRequest) =>
  restClient.put('finance/spreadsheet/remark', data);

// settings

const getDebtorSettings = (): Promise<DebtorSettings> => client.get('/finance/debtor/settings');

const getPaymentSettings = (): Promise<PaymentSettings> => client.get('finance/settings/payment');

const updateDebtorSettings = (data: UpdateDebtorSettingsRequest) =>
  client.put('finance/debtor/settings', data);

const activateReadFromMailbox = (): Promise<PurchaseInvoiceSettings> =>
  client.put('finance/settings/purchase-invoice/activate-reading');

const deActivateReadFromMailbox = (): Promise<PurchaseInvoiceSettings> =>
  client.put('finance/settings/purchase-invoice/deactivate-reading');

const getRepairSalesInvoices = (caseGroupId: string): Promise<RepairSalesInvoice[]> =>
  client.get(`/finance/outbound/repair/invoice/case-group/${caseGroupId}`);

const getOpenRepairSalesInvoicePaymentRequests = (): Promise<
  RepairSalesInvoicePaymentRequestStatus[]
> => client.get('/finance/outbound/repair/invoice/payment-request/open');

const updateRepairSalesInvoiceRemark = (data: UpdateFinanceRemarkRequest) =>
  client.put('/finance/outbound/repair/invoice/remark', data);

const updateRepairPaymentRequestRemark = (data: UpdateFinanceRemarkRequest) =>
  client.put('/finance/outbound/repair/invoice/payment-request/remark', data);

const linkRepairPaymentToRepairPaymentRequest = (
  data: LinkRepairPaymentToRepairPaymentRequestRequest,
) => client.post('/finance/outbound/repair/invoice/payment/link', data);

const createRepairSalesInvoice = (data: CreateRepairSalesInvoiceRequest) =>
  client.post('/finance/outbound/repair/invoice', data);

const deleteRepairSalesInvoice = (id: string) =>
  client.delete(`/finance/outbound/repair/invoice/${id}`);

const createRepairSalesInvoicePayment = (data: CreateRepairSalesInvoicePaymentRequest) =>
  client.post('/finance/outbound/repair/invoice/payment', data);

const deleteRepairSalesInvoicePayment = (id: string) =>
  client.delete(`/finance/outbound/repair/invoice/payment/${id}`);

const markRepairSalesInvoicePaymentRequestAsPaid = (id: string) =>
  client.post(`/finance/outbound/repair/invoice/payment-request/${id}/mark-as-paid`);

const toggleRepairSalesInvoicePaymentRequestFlow = (id: string) =>
  client.post(`/finance/outbound/repair/invoice/payment-request/${id}/toggle-flow`);

const moveRepairSalesInvoicePaymentRequestToDebtCollection = (id: string) =>
  client.post(`/finance/outbound/repair/invoice/payment-request/${id}/move-to-debt-collection`);

const updateRepairSalesInvoicePaymentRequestFlowStatus = (
  data: UpdateRepairPaymentRequestFlowStatusRequest,
) => client.put('/finance/outbound/repair/invoice/payment-request/flow-status', data);

const setRepairSalesInvoicePaymentRequestOnHold = (data: SetRepairPaymentRequestOnHoldRequest) =>
  client.put('/finance/outbound/repair/invoice/payment-request/on-hold', data);

const getProvisionSettings = (): Promise<ProvisionSettingsType[]> =>
  client.get('/finance/settings/provision');

const createProvisionSettings = (data: ProvisionSettingsFormFields) =>
  client.post('/finance/settings/provision', data);

const updateProvisionSettings = (data: ProvisionSettingsVariables) =>
  client.put(`/finance/settings/provision/${data.id}`, data);

const deleteProvisionSettings = (data: ProvisionSettingsDeleteRequest) =>
  client.delete('/finance/settings/provision', { data });

const getAcceptedInvoices = (
  request: FilteredAcceptedInvoicesPagedRequest,
): Promise<PagedResult<AcceptedInvoiceResponse>> =>
  client.get('/finance/inbound/hoa/accepted', { params: request });

const createEnvelope = (data: CreateEnvelopeRequest) =>
  client.post('/finance/inbound/hoa/envelope', data);

const downloadDocumentsForInvoicesAsZip = (invoiceIds: string[]): Promise<Blob> =>
  restClient.get('/finance/invoicing/inbound/hoa/invoice/documents.zip', {
    params: { invoiceIds },
    paramsSerializer: (params) => qs.stringify(params),
    responseType: 'blob',
  });

const uploadScans = (data: UploadScansRequest) => restClient.post<string[]>('/finance/scan', data);

const uploadUbls = (data: FormData): Promise<CreateInboundHoaUblUploadResponse[]> =>
  restClient.post<CreateInboundHoaUblUploadResponse[]>(
    '/finance/invoicing/inbound/hoa/invoice/ubl-upload',
    data,
  );

const uploadInboundHoaInvoiceDocuments = (formData: FormData) =>
  restClient.post<void>('/finance/invoicing/inbound/hoa/invoice/documents', formData);

export default {
  activateReadFromMailbox,
  createEnvelope,
  createManualCorrection,
  createManualPayment,
  createProvisionSettings,
  createRepairSalesInvoice,
  createRepairSalesInvoicePayment,
  deActivateReadFromMailbox,
  deletePayment,
  deleteProvisionSettings,
  deleteRepairSalesInvoice,
  deleteRepairSalesInvoicePayment,
  deleteSpreadsheet,
  downloadDocumentsForInvoicesAsZip,
  getAcceptedInvoices,
  getCaseGroupBalance,
  getDebtorSettings,
  getManualCorrections,
  getOpenRepairSalesInvoicePaymentRequests,
  getPaymentSettings,
  getProvisionSettings,
  getRepairSalesInvoices,
  getSpreadsheet,
  getSpreadsheetFiles,
  getSpreadsheetsByCaseGroup,
  linkRepairPaymentToRepairPaymentRequest,
  markRepairSalesInvoicePaymentRequestAsPaid,
  moveRepairSalesInvoicePaymentRequestToDebtCollection,
  setRepairSalesInvoicePaymentRequestOnHold,
  toggleRepairSalesInvoicePaymentRequestFlow,
  updateDebtorSettings,
  updateProvisionSettings,
  updateRepairPaymentRequestRemark,
  updateRepairSalesInvoicePaymentRequestFlowStatus,
  updateRepairSalesInvoiceRemark,
  updateSpreadsheetPublicity,
  updateSpreadsheetRemark,
  uploadInboundHoaInvoiceDocuments,
  uploadScans,
  uploadUbls,
};
